import React, { useState, FC } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleNotch, faDownload } from "@fortawesome/free-solid-svg-icons";
import html2pdf from "html2pdf.js";
import { handleEnterKeyToClick } from "../../common/accessibilityHelpers";
import { AUDITEVENTTYPE } from "services/trackService";
import { sendCustomEvent } from "common/utility";
import { usePoliciesData } from "hooks/usePoliciesData";
import { Box, Stack, Typography } from "@mui/material";
import { useTrackPageEffect } from "hooks/services/useTrackPageEffect";
import { useCardData } from "hooks/useCardData";
import { AuditEvent } from "types/track/AuditEventType";

const IdCard: FC = () => {
  const { data: { docIds = [], front = "", back = "" } = {}, isLoading } =
    useCardData();
  const [downloading, setDownloading] = useState(false);
  const { policies } = usePoliciesData();

  useTrackPageEffect({
    eventType: AuditEvent.MemberPortalViewIdCard,
  });

  const html2pdfOptions = {
    margin: 1,
    filename: `Alighment Health Plan ${
      policies && policies.length && policies[0].policy_name
    } ID Card.pdf`,
    image: { type: "jpeg", quality: 0.98 },
    html2canvas: { scale: 2 },
    jsPDF: { unit: "in", format: "letter", orientation: "portrait" },
  };

  return (
    <Stack my={2}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h2" component="h1" fontWeight="bold">
          My ID Card
        </Typography>
        <Box>
          {front && (
            <Stack
              onClick={async () => {
                let element = document.getElementById("id-card-display");
                if (!downloading && element) {
                  setDownloading(true);
                  sendCustomEvent({
                    eventType: AUDITEVENTTYPE.MemberPortalDownloadCard,
                  });
                  await html2pdf().set(html2pdfOptions).from(element).save();
                  setDownloading(false);
                }
              }}
              onKeyUp={handleEnterKeyToClick}
              tabIndex={0}
              aria-label="Click to download ID Card"
            >
              <Stack direction="row" alignItems="center" spacing={1}>
                <Typography>Download</Typography>
                <FontAwesomeIcon
                  className="float-right mr-2"
                  icon={downloading ? faCircleNotch : faDownload}
                  aria-label={downloading ? "Downloading" : "Click to Download"}
                  color="#333"
                  size="2x"
                />
              </Stack>
            </Stack>
          )}
        </Box>
      </Stack>
      <Stack
        my={2}
        direction={{
            xs: "column",
            md: "row",
        }}
        id="id-card-display"
        alignItems="center"
        spacing={2}
      >
        <Stack flex={{
            xs: 'auto',
            md: 1,
        }} >
          {isLoading ? (
            <Stack justifyContent="center" alignItems="center" height="375px">
              <FontAwesomeIcon
                icon={faCircleNotch}
                aria-label="Currently Loading Content"
                size="3x"
              />
            </Stack>
          ) : (
            <Box
              component="img"
              id={docIds && docIds[0]}
              className="card-shadow"
              src={front}
              width="100%"
              alt="Access Card front side"
            />
          )}
        </Stack>
        <Stack flex={{
            xs: 'auto',
            md: 1,
        }}>
          {isLoading ? (
            <Stack justifyContent="center" alignItems="center" height="375px">
              <FontAwesomeIcon
                icon={faCircleNotch}
                aria-label="Currently Loading Content"
                size="3x"
              />
            </Stack>
          ) : (
            <Box
              component="img"
              id={docIds && docIds[1]}
              className="card-shadow"
              src={back}
              width="100%"
              alt="Access Card front side"
            />
          )}
        </Stack>
      </Stack>
      <Stack spacing={2}>
        <Typography fontWeight="bold" variant="h5">
          What if the information on my card is wrong?
        </Typography>
        <Typography component="p">
          If any information on your member ID card is wrong, call your 24/7
          ACCESS On-Demand Concierge at your earliest convenience at{" "}
          <strong>1-833-242-2223</strong> (TTY 711). We will be happy to fix any
          errors and send you a new one.
        </Typography>
        <Typography fontWeight="bold" variant="h5">
          What if I lose my member ID card?{" "}
        </Typography>
        <Typography component="p">
          If you lose your ID card and need to show it for care, you can show a
          printed copy instead. To request a new ID card, or if you are using a
          screen reader and need assistance, please call your 24/7 ACCESS
          On-Demand Concierge at <strong>1-833-242-2223</strong> (TTY 711).
        </Typography>
      </Stack>
    </Stack>
  );
};

export default IdCard;
